/**
 * AppSwitcher — brand-row chevron that opens a Radix Popover for jumping
 * between K-OS apps.
 *
 * "Pattern A" app switching: the sidebar brand mark/name is the CURRENT app
 * (glyph + name, click → that app's home), and this compact chevron button
 * sitting at the right end of the brand row opens a menu listing every app.
 * The current app (`currentSlug`) is highlighted with the accent-soft state
 * and a trailing check; the others navigate to their `routeBase` and close
 * the popover. A footer item drops the user on the full `/apps` selector.
 */

import { useNavigate } from '@tanstack/react-router';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@k-os/ui';
import { PLATFORM_APPS, APP_SLUGS } from '@k-os/core';
import { AppIcon } from './AppIcon';
import styles from './AppSwitcher.module.css';

export interface AppSwitcherProps {
  /** Slug of the app the user is currently in (`'platform'` on /apps). */
  currentSlug: string;
}

export function AppSwitcher({ currentSlug }: AppSwitcherProps) {
  const navigate = useNavigate();

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className={styles.trigger} aria-label="Switch app">
          <Icon name="chevronDown" size={14} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={styles.content}
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={20}
        >
          <div className={styles.header}>Switch app</div>
          <div className={styles.list}>
            {APP_SLUGS.map((slug) => {
              const app = PLATFORM_APPS[slug];
              const current = slug === currentSlug;
              const row = (
                <button
                  type="button"
                  className={`${styles.item} ${current ? styles.itemCurrent : ''}`}
                  onClick={() => void navigate({ to: app.routeBase })}
                  aria-current={current ? 'true' : undefined}
                >
                  <span className={styles.itemGlyph}>
                    <AppIcon slug={slug} size={26} />
                  </span>
                  <span className={styles.itemText}>
                    <span className={styles.itemName}>{app.name}</span>
                    <span className={styles.itemDesc}>{app.description}</span>
                  </span>
                  {current && (
                    <span className={styles.itemCheck}>
                      <Icon name="check" size={14} />
                    </span>
                  )}
                </button>
              );
              // The current app still navigates to its own home — harmless —
              // so every row closes the popover the same way.
              return (
                <Popover.Close key={slug} asChild>
                  {row}
                </Popover.Close>
              );
            })}
          </div>
          <div className={styles.divider} />
          <Popover.Close asChild>
            <button
              type="button"
              className={styles.footerItem}
              onClick={() => void navigate({ to: '/apps' })}
            >
              <span className={styles.footerIcon}>
                <Icon name="sparkles" size={13} />
              </span>
              <span>Browse all apps</span>
            </button>
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
